import { useSyncExternalStore } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 30 * 1000;

let outdated = false;
let lastCheck = 0;
const listeners = new Set<() => void>();

const markOutdated = () => {
  if (outdated) return;
  outdated = true;
  listeners.forEach((l) => l());
};

async function check(): Promise<void> {
  if (outdated || Date.now() - lastCheck < MIN_CHECK_GAP_MS) return;
  lastCheck = Date.now();
  try {
    const res = await fetch(`/version.json?t=${lastCheck}`, { cache: 'no-store' });
    if (!res.ok) return;
    const { build } = (await res.json()) as { build?: unknown };
    if (typeof build === 'string' && build !== __BK_BUILD_ID__) markOutdated();
  } catch {
    // Rete assente o deploy in corso: si riprova al controllo successivo.
  }
}

/**
 * Aggiornamento dopo un deploy. La pagina non si ricarica mai mentre l'utente scrive: quando online
 * c'è una build diversa, il primo cambio di pagina diventa un caricamento completo della pagina di
 * destinazione con la nuova versione. Nel frattempo un avviso permette di aggiornare subito.
 */
export function watchAppVersion(): void {
  if (!import.meta.env.PROD) return;
  window.addEventListener('hashchange', () => {
    if (outdated) window.location.reload();
    else void check();
  });
  // Un pezzo della build precedente non più disponibile indica una versione nuova online.
  window.addEventListener('vite:preloadError', markOutdated);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  window.setInterval(() => void check(), CHECK_INTERVAL_MS);
  void check();
}

export function useAppOutdated(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => outdated,
  );
}
